import { useEffect, useState } from "react";
import {
  Box,
  Text,
  Button,
  useMediaQuery,
} from "@chakra-ui/react";
import { fetchSubscriptionPlansAction, fetchPlanByIdAction } from "../../Actions/B2CSubscription.action";
import {
  createB2CSubscriptionHandler,
  openRazorpayCheckout,
} from "../../Actions/B2CPayment.action";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { saveUser } from "../../../../../Redux/User-slice/User-slice";
import Navbar from "../../../../../GlobalComponents/Navbar/Navbar";
import { CouponValidator } from "../CouponValidator/CouponValidator";
import PlanCard from "./PlanCard";

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface SubscriptionDetailsInterface {
  subscription_id: string;
  plan_id: string;
  status:
  | "created"
  | "authenticated"
  | "active"
  | "halted"
  | "cancelled"
  | "completed";
  short_url: string;
  user_id: string;
  subscriber_id: string;
}

export interface SubscriptionPlanInterface {
  _id?: string;
  planName: string;
  description: string;
  planId: string;
  amount: string;
  period: "daily" | "weekly" | "monthly" | "yearly";
  interval: number;
  active: boolean;
  companyId?: string | null;
  maxBeneficiaries?: number;
  planCategory?: string | null;
  coverageTitle?: string | null;
  coverageDescription?: string | null;
  highlights?: string[];
  includesText?: string | null;
  themeColor?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Payment Form Component
const PaymentForm = ({ isMobile, benCount, validCoupon, setValidCoupon, selectedPlan, HandlePayment, isLoading }: any) => (
  <Box
    bg="white"
    boxShadow="md"
    w={isMobile ? "full" : "380px"}
    mx={isMobile ? "0" : "auto"}
    border="1px solid #e8e8e8"
    borderRadius="16px"
    p={isMobile ? "20px" : "24px 24px"}
    display="flex"
    flexDirection="column"
    justifyContent="center"
    gap={isMobile ? "16px" : "20px"}
  >
    {/* Header */}
    <Box textAlign="center">
      <Text
        fontFamily="Poppins"
        fontSize={isMobile ? "16px" : "18px"}
        fontWeight="600"
        color="#333"
      >
        Order Summary
      </Text>
      <Box w="40px" h="3px" bg="#00A7B5" mx="auto" mt="6px" borderRadius="2px" />
    </Box>

    {/* Selected Plan Info */}
    <Box
      bg="#f8fffe"
      borderRadius="8px"
      p="12px"
      border="1px solid #e0f5f5"
    >
      <Text fontFamily="Poppins" fontSize="12px" color="#666" mb="2px">
        Selected Plan
      </Text>
      <Text fontFamily="Poppins" fontSize="14px" fontWeight="600" color="#333">
        {selectedPlan?.planName || "—"} {selectedPlan?.planCategory ? `(${selectedPlan.planCategory})` : ""}
      </Text>
    </Box>

    {/* Amount */}
    <Box>
      <Text fontFamily="Poppins" fontSize="13px" color="#666" mb="8px">
        Total Amount
      </Text>
      <Text fontFamily="Poppins" fontSize={isMobile ? "24px" : "26px"} fontWeight="700" color="#333">
        ₹{validCoupon ?
          (() => {
            const totalAmount = benCount * Number(selectedPlan?.amount);
            const percentageDiscount = totalAmount * validCoupon.discountWorth / 100;
            const flatDiscount = validCoupon.discountWorth * 100;
            const maxDiscount = validCoupon.maximumDiscount * 100;
            let discount;
            if (validCoupon.discountType === 'percentage') {
              discount = Math.min(percentageDiscount, maxDiscount);
            } else {
              discount = Math.min(flatDiscount, maxDiscount);
            }
            return ((totalAmount - discount) / 100).toLocaleString();
          })() :
          (benCount * Number(selectedPlan?.amount) / 100).toLocaleString()}
        <Text as="span" fontSize="14px" fontWeight="400" color="#666">
          {" "}/ {selectedPlan?.period || 'year'}
        </Text>
      </Text>
      {validCoupon && (
        <Box mt="4px">
          <Text as="span" textDecoration="line-through" color="gray.400" fontSize="14px" fontFamily="Poppins">
            ₹{(benCount * Number(selectedPlan?.amount) / 100).toLocaleString()}
          </Text>
          <Text as="span" color="green.500" fontSize="13px" fontFamily="Poppins" ml="8px">
            You save ₹{(() => {
              const totalAmount = benCount * Number(selectedPlan?.amount);
              const percentageDiscount = totalAmount * validCoupon.discountWorth / 100;
              const flatDiscount = validCoupon.discountWorth * 100;
              const maxDiscount = validCoupon.maximumDiscount * 100;
              let discount;
              if (validCoupon.discountType === 'percentage') {
                discount = Math.min(percentageDiscount, maxDiscount);
              } else {
                discount = Math.min(flatDiscount, maxDiscount);
              }
              return (discount / 100).toLocaleString();
            })()}
          </Text>
          {validCoupon.redemptionType === 'single_use' && (
            <Text fontSize="11px" color="orange.500" mt="4px" fontFamily="Poppins">
              * Discount applies to first year only
            </Text>
          )}
        </Box>
      )}
    </Box>

    {/* Divider */}
    <Box h="1px" bg="#eee" />

    {/* Coupon */}
    <CouponValidator onCouponValidated={setValidCoupon} />

    {/* Pay Button */}
    <Button
      bg="#00A7B5"
      color="white"
      w="full"
      h={isMobile ? "44px" : "48px"}
      fontFamily="Poppins"
      fontSize={isMobile ? "14px" : "16px"}
      fontWeight="600"
      onClick={HandlePayment}
      isLoading={isLoading}
      borderRadius="10px"
      _hover={{ bg: "#008a96" }}
      boxShadow="0 4px 12px rgba(0, 167, 181, 0.3)"
    >
      Pay Now
    </Button>
  </Box>
);

const PlanDetails = ({ setStep }: any) => {
  const [isMobile] = useMediaQuery("(max-width: 480px)");
  const [isTab] = useMediaQuery("(max-width: 768px)");
  const userData: any = useSelector((state: any) => state.user);
  const [plans, setPlans] = useState<SubscriptionPlanInterface[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanInterface>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [benCount] = useState<number>(1);
  const [validCoupon, setValidCoupon] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRenewal = searchParams.get('isRenewal') === 'true';
  const urlPlanId = searchParams.get('planId');

  useEffect(() => {
    const fetchData = async () => {
      try {
        let res: any;
        if (urlPlanId) {
          const planRes = await fetchPlanByIdAction(urlPlanId);
          res = planRes ? [planRes] : [];
        } else {
          res = await fetchSubscriptionPlansAction();
        }
        setPlans(res);
        setSelectedPlan(res[0]);
      } catch (error) {
        console.error("Error fetching subscription plans:", error);
      }
    };
    fetchData();
  }, [urlPlanId]);

  const selectPlanHandler = (plan: SubscriptionPlanInterface) => {
    setSelectedPlan(plan);
  };

  const paymentResponseHandler = async (response: any) => {
    const maxBen = (selectedPlan as any)?.maxBeneficiaries || 0;
    dispatch(saveUser({ ...userData, numberOfBenAllowed: maxBen }));
    navigate(`/subscription/payment-confirmation/${response.razorpay_order_id}/${response.razorpay_payment_id}/${maxBen}${isRenewal ? '?isRenewal=true' : ''}`);
  };

  const HandlePayment = async () => {
    setIsLoading(true);
    if (!selectedPlan) {
      alert("Please Select Plan");
      setIsLoading(false);
      return;
    }
    let subscriptionData: SubscriptionDetailsInterface =
      await createB2CSubscriptionHandler({
        planId: selectedPlan.planId,
        quantity: benCount,
        ...(validCoupon && { offerId: validCoupon.offerId }),
      });
    if (!subscriptionData) {
      setIsLoading(false);
      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      name: "Pococare",
      order_id: `${subscriptionData.subscription_id}`,
      handler: paymentResponseHandler,
      prefill: {
        name: userData.name,
        email: userData.email,
        contact: userData.mobileNo?.split('-')[1],
      },
      notes: { order_by: "order", ref_id: subscriptionData.subscription_id },
      theme: {
        color: "#F37254",
      },
    };

    openRazorpayCheckout(options);
    setIsLoading(false);
  };

  return (
    <>
      <Navbar />
      <Box
        mt={isMobile ? "80px" : isTab ? "100px" : "120px"}
        bgGradient="linear(180deg, #FFF 0%, #D2F0F2 100%)"
        minHeight={"100vh"}
        overflow={"hidden"}
      >
        <Box
          display={"flex"}
          flexDir={isMobile ? "column" : "row"}
          justifyContent={"center"}
          alignItems={"flex-start"}
          gap={"24px"}
          p={isMobile ? "16px" : "40px"}
          backgroundImage="url('/Images/image_prev_ui.svg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
          maxW="1200px"
          mx="auto"
        >
          {/* Mobile: Payment Form at Top */}
          {isMobile && (
            <PaymentForm
              isMobile={isMobile}
              benCount={benCount}
              validCoupon={validCoupon}
              setValidCoupon={setValidCoupon}
              selectedPlan={selectedPlan}
              HandlePayment={HandlePayment}
              isLoading={isLoading}
            />
          )}

          {/* Plan Cards - Left Side */}
          <Box
            display="flex"
            flexDir={isMobile ? "column" : isTab ? "column" : "row"}
            gap="20px"
            alignItems="stretch"
            justifyContent="center"
          >
            {plans.map((val: any) => (
              <PlanCard
                key={val.planId || val._id}
                plan={val}
                isSelected={selectedPlan?._id === val._id || selectedPlan?.planId === val.planId}
                onSelect={selectPlanHandler}
              />
            ))}
          </Box>

          {/* Desktop: Payment Form - Right Side */}
          {!isMobile && (
            <Box alignSelf="center">
              <PaymentForm
                isMobile={isMobile}
                benCount={benCount}
                validCoupon={validCoupon}
                setValidCoupon={setValidCoupon}
                selectedPlan={selectedPlan}
                HandlePayment={HandlePayment}
                isLoading={isLoading}
              />
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default PlanDetails;
